import { useState } from 'react';
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ProcessorEditHeader } from '../../components/processor-edit-header';

import { ProcessorDetails, useProcessorById, useUpdateProcessor } from '@/lib/api/processors';
import { ProcessorDryModeField } from '../../components/processor-dry-mode-field';
import { ErrorDisplay } from '@/components/ui/error-display';
import { parseTRPCError } from '@/lib/utils/trpc-error-utils';
import { useNavigate } from 'react-router-dom';
import { useProcessorEditId } from '@/app/processors/hooks/use-processor-edit-id';
import { useProcessorEditExit } from '@/app/processors/hooks/use-processor-edit-exit';
import { useToast } from '@/components/ui/use-toast';
import { SecretInput } from '@/components/form/secret-input';
import { brandingConfig } from '@/config/branding';

interface OvriEditPageProps {
  processor: ProcessorDetails;
}

const OvriEditPageContainerInner = () => {
  const { t } = useTranslation('processors');
  const id = useProcessorEditId();
  const { data, isLoading, error } = useProcessorById(id!);

  if (isLoading) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return <ErrorDisplay error={parseTRPCError(error)} />;
  }

  if (!data) {
    return <div className="container mx-auto py-8">{t('processorNotFound')}</div>;
  }

  return <OvriEditPage processor={data} />;
};

const OvriEditPage = ({ processor }: OvriEditPageProps) => {
  const { t } = useTranslation('processors');
  const navigate = useNavigate();
  const processorExit = useProcessorEditExit();
  const { toast } = useToast();

  const [validationError, setValidationError] = useState<string | null>(null);

  const updateProcessorMutation = useUpdateProcessor();

  const [formData, setFormData] = useState({
    name: processor.name || '',
    enabled: processor.enabled ?? true,
    testMode: (processor.options as any)?.testMode ?? false,
    dryMode: (processor.options as any)?.dryMode === true,
    apiKey: (processor.options as any)?.apiKey || '',
    websiteKey: (processor.options as any)?.websiteKey || '',
    secretKeyPos: (processor.options as any)?.secretKeyPos || '',
  });

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.apiKey) {
      setValidationError(t('ovri.credentials.apiKeyRequired'));
      return;
    }

    if (!formData.websiteKey) {
      setValidationError(t('ovri.credentials.websiteKeyRequired'));
      return;
    }

    if (!formData.secretKeyPos) {
      setValidationError(t('ovri.credentials.secretKeyPosRequired'));
      return;
    }

    try {
      await updateProcessorMutation.mutateAsync({
        processorId: processor.id,
        updatedData: {
          type: 'ovri',
          name: formData.name,
          enabled: formData.enabled,
          options: {
            testMode: formData.testMode,
            dryMode: formData.dryMode,
            apiKey: formData.apiKey,
            websiteKey: formData.websiteKey,
            secretKeyPos: formData.secretKeyPos,
          },
        } as any,
      });

      toast({ description: t('processorUpdated') });
      if (processorExit.isEmbed) {
        processorExit.exitAfterSave();
        return;
      }
      navigate(-1);
    } catch (error) {
      console.error('Error saving processor:', error);
      toast({ description: t('failedToSave'), variant: 'destructive' });
    }
  };

  return (
    <div className="container mx-auto py-8">
      <ProcessorEditHeader
        processorId={processor.id}
        title={t('ovri.editTitle')}
        onCancel={() => (processorExit.isEmbed ? processorExit.exitCancel() : navigate(-1))}
        onSave={handleSubmit}
        isSaving={updateProcessorMutation.isPending}
      />

      {validationError && (
        <Alert variant="destructive" className="mb-6">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        {/* General Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('generalSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">{t('name')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('enterProcessorName')}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="enabled"
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked === true })}
              />
              <Label htmlFor="enabled">{t('enabled')}</Label>
            </div>
          </CardContent>
        </Card>

        {/* Test Mode Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('testModeSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="testMode"
                checked={formData.testMode}
                onCheckedChange={(checked) => setFormData({ ...formData, testMode: checked === true })}
              />
              <Label htmlFor="testMode">{t('enableTestMode')}</Label>
            </div>
            {formData.testMode && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                <p className="text-sm text-yellow-800">
                  ⚠️ {t('ovri.testMode.description')}
                </p>
                <a
                  href="https://api.ovri.app/documentation/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-block text-sm text-blue-600 underline hover:text-blue-800"
                >
                  {t('ovri.documentation')}
                </a>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ovri Settings */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>{t('ovri.credentials.apiKeyLabel')}</CardTitle>
              {brandingConfig.helpCenterUrl && (
                <a
                  href={`${brandingConfig.helpCenterUrl}/payment-processors/connect-ovri-account-on-tagada`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-semibold text-blue-600 underline hover:text-blue-800"
                >
                  {t('ovri.setupGuide')}
                </a>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {brandingConfig.helpCenterUrl && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <p className="text-sm text-blue-700">
                  {t('ovri.needHelp')}{' '}
                  <a
                    href={`${brandingConfig.helpCenterUrl}/payment-processors/connect-ovri-account-on-tagada`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium underline"
                  >
                    {t('ovri.setupGuide')}
                  </a>
                </p>
              </div>
            )}

            <div>
              <SecretInput
                label={t('ovri.credentials.apiKeyLabel')}
                value={formData.apiKey}
                onChange={(value) => setFormData({ ...formData, apiKey: value })}
                placeholder={t('ovri.credentials.apiKeyPlaceholder')}
              />
              <p className="text-muted-foreground mt-1 text-xs">
                {t('ovri.credentials.apiKeyDescription')}
              </p>
            </div>

            <div>
              <SecretInput
                label={t('ovri.credentials.websiteKeyLabel')}
                value={formData.websiteKey}
                onChange={(value) => setFormData({ ...formData, websiteKey: value })}
                placeholder={t('ovri.credentials.websiteKeyPlaceholder')}
              />
              <p className="text-muted-foreground mt-1 text-xs">
                {t('ovri.credentials.websiteKeyDescription')}
              </p>
            </div>

            <div>
              <SecretInput
                label={t('ovri.credentials.secretKeyPosLabel')}
                value={formData.secretKeyPos}
                onChange={(value) => setFormData({ ...formData, secretKeyPos: value })}
                placeholder={t('ovri.credentials.secretKeyPosPlaceholder')}
              />
              <p className="text-muted-foreground mt-1 text-xs">
                {t('ovri.credentials.secretKeyPosDescription')}
              </p>
            </div>
          </CardContent>
        </Card>

        <ProcessorDryModeField
          checked={formData.dryMode}
          onCheckedChange={(checked) => setFormData({ ...formData, dryMode: checked })}
          disabled={updateProcessorMutation.isPending}
        />
      </div>
    </div>
  );
};

export const OvriEditPageContainer = withRequiredPermission(
  OvriEditPageContainerInner,
  PERMISSIONS.PROCESSORS_MANAGE,
);
